import { CreateReportButton } from "@/components/CreateReportButton";
import { LastImportInfo } from "@/components/LastImportInfo";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, PieChart, Pie, Cell, Legend } from "recharts";

const COLORS = ["#4F46E5", "#14B8A6", "#F59E0B", "#F43F5E", "#0EA5E9"];

export default function KPI() {
  const [kpi, setKpi] = useState(null);
  const [activities, setActivities] = useState([]);
  const [sectors, setSectors] = useState([]);

  useEffect(() => {
    (async () => {
      const [{ data: k }, { data: a }, { data: s }] = await Promise.all([
        api.get("/kpi/summary"), api.get("/activities"), api.get("/sectors"),
      ]);
      setKpi(k); setActivities(a); setSectors(s);
    })();
  }, []);

  const perSector = sectors.map((s) => ({
    name: s.name,
    Visites: activities.filter((a) => a.sector_id === s.id).length,
  }));

  const perUser = (kpi?.per_user || []).map((r) => ({ name: r.user.name.split(" ")[0], visits: r.visits, plans: r.open_plans }));

  const donutData = [
    { name: "Résolues", value: kpi?.resolved_issues || 0 },
    { name: "Ouvertes", value: kpi?.open_issues || 0 },
  ];

  return (
    <div className="space-y-6" data-testid="kpi-page">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">KPI automatiques</h1>
        <p className="text-slate-500 mt-1">Indicateurs clés calculés en temps réel.</p>
        <LastImportInfo className="mt-2"/><CreateReportButton module="ops" className="mt-2 ml-2"/>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          ["Visites totales", kpi?.total_visits ?? 0, "text-indigo-600 bg-indigo-50"],
          ["Cette semaine", kpi?.visits_week ?? 0, "text-teal-600 bg-teal-50"],
          ["Magasins visités", kpi?.stores_visited ?? 0, "text-sky-600 bg-sky-50"],
          ["Temps terrain", `${Math.round((kpi?.time_on_field_min ?? 0) / 60)}h`, "text-amber-600 bg-amber-50"],
        ].map(([label, value, cls]) => (
          <div key={label} className="bg-white border border-slate-200 rounded-2xl p-5">
            <div className={`inline-flex text-[10px] font-semibold px-2.5 py-1 rounded-full ${cls}`}>{label}</div>
            <div className="font-display text-3xl font-bold text-slate-900 mt-3">{value}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-2xl p-6">
          <h2 className="font-display text-lg font-semibold text-slate-900">Visites par secteur</h2>
          <div className="h-64 mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={perSector}>
                <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="#94a3b8"/>
                <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8"/>
                <Tooltip contentStyle={{ borderRadius: 6, border: "1px solid #e2e8f0" }}/>
                <Bar dataKey="Visites" fill="#4F46E5" radius={[8, 8, 0, 0]}/>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-6">
          <h2 className="font-display text-lg font-semibold text-slate-900">Répartition problématiques</h2>
          <div className="h-64 mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={donutData} innerRadius={50} outerRadius={90} dataKey="value" paddingAngle={4}>
                  {donutData.map((_, i) => <Cell key={i} fill={COLORS[i]}/>)}
                </Pie>
                <Legend/>
                <Tooltip/>
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="text-center">
            <div className="font-display text-4xl font-bold text-indigo-600">{kpi?.resolution_rate ?? 0}%</div>
            <div className="text-xs font-semibold tracking-wider text-slate-500 uppercase">Taux de résolution</div>
          </div>
        </div>

        {perUser.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-2xl p-6 lg:col-span-2">
            <h2 className="font-display text-lg font-semibold text-slate-900">Activité par responsable</h2>
            <div className="h-64 mt-4">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={perUser}>
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} stroke="#94a3b8"/>
                  <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8"/>
                  <Tooltip contentStyle={{ borderRadius: 6, border: "1px solid #e2e8f0" }}/>
                  <Legend/>
                  <Bar dataKey="visits" name="Visites" fill="#4F46E5" radius={[8, 8, 0, 0]}/>
                  <Bar dataKey="plans" name="Plans" fill="#14B8A6" radius={[8, 8, 0, 0]}/>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
